'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import './globals.css';

/**
 * Root-level error UI when the root layout fails. Must include html/body (replaces root layout).
 * Import globals.css so Tailwind tokens apply when this boundary mounts.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.error('[global-error]', error);
    }
  }, [error]);

  return (
    <html lang="en">
      <body className="min-h-screen bg-background font-sans text-foreground antialiased">
        <div
          className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 py-12 text-center"
          role="alert"
        >
          <h1 className="text-xl font-semibold">Application error</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            A critical error occurred. Try reloading the page or return to the home screen.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => reset()}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Try again
            </button>
            <Link
              href="/"
              className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              Home
            </Link>
          </div>
        </div>
      </body>
    </html>
  );
}
